import { A11yModule, LiveAnnouncer } from '@angular/cdk/a11y';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Modal } from 'flowbite';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import {
  IBackofficeError,
  ICustomToggleOptions,
  IVehicleScheduleChange,
  IVehicleScheduleSaveSummary,
  IVehicleScheduleSummaryRow,
  IVehicleScheduleUpdateOutcome,
} from 'src/app/models';
import { CustomToggleTone, ScheduleSaveStep, ScheduleSummaryTab, ScheduleSummaryView } from 'src/app/models/enums';
import { LiteralService, UtilsService, VehiclesService, WebSocketService } from 'src/app/services';
import { ToggleComponent } from 'src/app/shared/input';
import { images } from 'src/images';
import { TooltipModule } from 'src/app/shared/tooltip/tooltip.module';
import { CustomToggleComponent } from '../../custom-toggle/custom-toggle.component';
import { ServiceTagComponent } from '../../service-tag/service-tag.component';
import { scheduleUpdateRequest } from '../scheduleChanges';
import { scheduleSaveSummary } from '../scheduleSaveSummary';

@Component({
  selector: 'app-vehicle-schedule-save-modal',
  templateUrl: './vehicle-schedule-save-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, CustomToggleComponent, LottieComponent, ServiceTagComponent, ToggleComponent, TooltipModule],
})
export class VehicleScheduleSaveModalComponent {
  readonly changes = input.required<IVehicleScheduleChange[]>();
  readonly summaryClosedEventEmitter = output<void>();

  private readonly saveScheduleModalElement = viewChild.required<ElementRef<HTMLElement>>('saveScheduleModal');
  private saveScheduleModal?: Modal;

  readonly ScheduleSaveStep = ScheduleSaveStep;
  readonly ScheduleSummaryTab = ScheduleSummaryTab;
  readonly ScheduleSummaryView = ScheduleSummaryView;
  readonly summaryTabs = [ScheduleSummaryTab.AVAILABILITIES, ScheduleSummaryTab.TRIPS];
  readonly summaryViewToggleOptions: ICustomToggleOptions<ScheduleSummaryView> = [
    {
      value: ScheduleSummaryView.FAILED,
      image: images.alert,
      descriptionKey: 'vehicles.actions.saveSchedule.showFailed',
      tone: CustomToggleTone.DANGER,
    },
    {
      value: ScheduleSummaryView.SUCCEEDED,
      image: images.checkCircle,
      descriptionKey: 'vehicles.actions.saveSchedule.showSucceeded',
      tone: CustomToggleTone.SUCCESS,
    },
  ];
  readonly images = images;
  readonly loadingAnimation: AnimationOptions = { path: '/assets/animations/bus.json' };
  readonly summaryTabLabelKeys: Record<ScheduleSummaryTab, string> = {
    [ScheduleSummaryTab.AVAILABILITIES]: 'vehicles.actions.saveSchedule.availabilities',
    [ScheduleSummaryTab.TRIPS]: 'vehicles.actions.saveSchedule.trips',
  };
  private readonly summaryTitleKeys: Record<ScheduleSummaryView, string> = {
    [ScheduleSummaryView.FAILED]: 'vehicles.actions.saveSchedule.failedTitle',
    [ScheduleSummaryView.SUCCEEDED]: 'vehicles.actions.saveSchedule.succeededTitle',
  };
  private readonly summaryViewKeys: Record<ScheduleSummaryView, keyof IVehicleScheduleSaveSummary> = {
    [ScheduleSummaryView.FAILED]: 'failed',
    [ScheduleSummaryView.SUCCEEDED]: 'succeeded',
  };

  readonly saveScheduleShowed = signal(false);
  readonly step = signal(ScheduleSaveStep.CONFIRM);
  readonly saveInProgress = computed(() => this.saveScheduleShowed() && this.step() !== ScheduleSaveStep.CONFIRM);
  readonly sendNotification = signal(true);
  readonly progress = signal(0);
  readonly progressDetail = signal('');
  readonly operationError = signal<string | undefined>(undefined);
  readonly summaryView = signal(ScheduleSummaryView.FAILED);
  readonly summaryTab = signal(ScheduleSummaryTab.AVAILABILITIES);
  private readonly outcome = signal<IVehicleScheduleUpdateOutcome | undefined>(undefined);
  private readonly submittedChanges = signal<IVehicleScheduleChange[]>([]);

  readonly title = computed(() =>
    this.step() === ScheduleSaveStep.SUMMARY
      ? this.summaryTitleKeys[this.summaryView()]
      : 'vehicles.actions.saveSchedule.title',
  );
  readonly failedView = computed(() => this.summaryView() === ScheduleSummaryView.FAILED);
  private readonly summary = computed(() =>
    scheduleSaveSummary(this.outcome() ?? { succeeded: [], failures: [] }, this.submittedChanges()),
  );
  private readonly viewRows = computed(() => this.summary()[this.summaryViewKeys[this.summaryView()]]);
  readonly tabCounts = computed<Record<ScheduleSummaryTab, number>>(() => ({
    [ScheduleSummaryTab.AVAILABILITIES]: this.viewRows().availabilities.length,
    [ScheduleSummaryTab.TRIPS]: this.viewRows().trips.length,
  }));
  readonly visibleRows = computed(() => {
    const rows = this.viewRows();
    const tabRows = this.summaryTab() === ScheduleSummaryTab.AVAILABILITIES ? rows.availabilities : rows.trips;
    return tabRows.map((row) => ({ ...row, reason: this.failureReason(row) }));
  });

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private vehiclesService: VehiclesService,
    private webSocketService: WebSocketService,
    private liveAnnouncer: LiveAnnouncer,
  ) {}

  showSaveScheduleModal() {
    this.step.set(ScheduleSaveStep.CONFIRM);
    this.sendNotification.set(true);
    this.outcome.set(undefined);
    this.operationError.set(undefined);
    this.saveScheduleModal = new Modal(this.saveScheduleModalElement().nativeElement, {
      placement: 'center',
      closable: false,
      onHide: () => this.saveScheduleShowed.set(false),
      onShow: () => this.saveScheduleShowed.set(true),
    });
    this.saveScheduleModal.toggle();
  }

  closeSaveScheduleModal() {
    if (this.step() === ScheduleSaveStep.SAVING) return;
    this.saveScheduleModal?.hide();
    if (this.step() === ScheduleSaveStep.SUMMARY) {
      this.summaryClosedEventEmitter.emit();
    }
  }

  toggleSummaryView() {
    this.summaryView.set(this.failedView() ? ScheduleSummaryView.SUCCEEDED : ScheduleSummaryView.FAILED);
  }

  async saveSchedule() {
    const changes = this.changes();
    this.submittedChanges.set(changes);
    this.progress.set(0);
    this.progressDetail.set('');
    this.step.set(ScheduleSaveStep.SAVING);
    try {
      const outcome = await this.vehiclesService.applySchedule(
        scheduleUpdateRequest(changes, this.sendNotification()),
        (message) => this.onProgress(message),
      );
      this.showSummary(outcome);
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        this.step.set(ScheduleSaveStep.CONFIRM);
        return;
      }
      this.operationError.set(this.operationErrorMessage(error));
      this.showSummary({ succeeded: [], failures: [] });
    }
  }

  private onProgress(message: any) {
    this.progress.set(message?.total_percentage ?? this.progress());
    if (this.webSocketService.isIntermediatePhaseCompletion(message)) return;
    this.progressDetail.set(this.webSocketService.progressDetail(message));
  }

  private showSummary(outcome: IVehicleScheduleUpdateOutcome) {
    this.outcome.set(outcome);
    const hasFailures = outcome.failures.length > 0 || this.operationError() !== undefined;
    this.summaryView.set(hasFailures ? ScheduleSummaryView.FAILED : ScheduleSummaryView.SUCCEEDED);
    this.summaryTab.set(
      this.tabCounts()[ScheduleSummaryTab.AVAILABILITIES] || !this.tabCounts()[ScheduleSummaryTab.TRIPS]
        ? ScheduleSummaryTab.AVAILABILITIES
        : ScheduleSummaryTab.TRIPS,
    );
    this.step.set(ScheduleSaveStep.SUMMARY);
    this.liveAnnouncer.announce(
      this.literalService.get('vehicles.actions.saveSchedule.announcement', true, {
        saved: outcome.succeeded.length,
        notSaved: outcome.failures.length,
      }),
    );
  }

  private failureReason(row: IVehicleScheduleSummaryRow): string {
    return this.failedView() ? this.errorMessage(row.errorCode ?? 0) : '';
  }

  private operationErrorMessage(error: unknown): string {
    const errorCode = (error as { error_code?: number } | undefined)?.error_code;
    if (errorCode === undefined) return this.literalService.get('vehicles.actions.saveSchedule.trackingLost', true);
    return this.errorMessage(errorCode);
  }

  private errorMessage(errorCode: IBackofficeError['errorCode']): string {
    const key = `${errorCode}`;
    const translated = this.literalService.get(key);
    return translated && translated !== key ? this.literalService.get(key, true) : this.literalService.get('0', true);
  }
}
